/**
 * Transaction Manager Component
 * Allows admins to view, approve (reconcile), or reject (flag) transactions
 */

import React, { useState, useEffect } from 'react';
import { 
  CheckCircle2, 
  XCircle, 
  Clock, 
  Filter, 
  Search,
  ChevronLeft,
  ChevronRight,
  ArrowUpCircle,
  ArrowDownCircle,
  Building2,
  Calendar,
  RefreshCw,
  AlertTriangle,
  Image as ImageIcon,
  Eye,
  X,
  CreditCard,
  User,
  FileText
} from 'lucide-react';
import { transactionsApi, companiesApi } from '../../services/api.service';
import type { TransactionStatus, TransactionType } from '../../types/api.types';

// Transaction type for this component
interface Transaction {
  transaction_id: string;
  company_id: string;
  account_id: string;
  user_id: string | null;
  date: string;
  description: string;
  amount: number;
  units: number | null;
  type: TransactionType;
  status: TransactionStatus;
  reference: string | null;
  imageUrl: string | null;
  createdAt: string;
  updatedAt: string;
  company?: {
    company_id: string;
    name: string;
  };
  bankAccount?: {
    account_id: string;
    bankName: string;
    accountNumber: string;
    currency?: string;
  };
  submittedBy?: {
    user_id: string | null;
    name: string | null;
  };
}

interface Company {
  company_id: string;
  name: string;
}

type StatusFilter = 'ALL' | TransactionStatus;

export const TransactionManager: React.FC = () => {
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Stats
  const [stats, setStats] = useState({ pending: 0, reconciled: 0, flagged: 0, total: 0 });
  
  // Filters
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('ALL');
  const [companyFilter, setCompanyFilter] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const pageSize = 20;

  // Selected transactions for bulk actions
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // Selected transaction for detail modal
  const [selectedTransaction, setSelectedTransaction] = useState<Transaction | null>(null);

  // Fetch stats
  const fetchStats = async () => {
    try {
      const response = await transactionsApi.getStats();
      console.log('Stats API response:', response);
      if (response) {
        setStats({
          pending: response.pending || 0,
          reconciled: response.reconciled || 0,
          flagged: response.flagged || 0,
          total: response.total || 0
        });
      }
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
  };

  // Fetch companies for filter dropdown
  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        const response = await companiesApi.getAll();
        const data = response?.data || response || [];
        setCompanies(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error('Failed to fetch companies:', err);
      }
    };
    fetchCompanies();
    fetchStats();
  }, []);

  // Fetch transactions
  const fetchTransactions = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const filters: Record<string, any> = {
        page: currentPage,
        limit: pageSize,
      };
      
      if (statusFilter !== 'ALL') {
        filters.status = statusFilter;
      }
      if (companyFilter) {
        filters.companyId = companyFilter;
      }
      if (dateFrom) {
        filters.startDate = dateFrom;
      }
      if (dateTo) {
        filters.endDate = dateTo;
      }
      if (searchQuery) {
        filters.search = searchQuery;
      }

      const response = await transactionsApi.getAll(filters);
      
      console.log('Transactions API response:', response);
      
      // Handle response structure - API returns { data, meta }
      const data = response?.data || [];
      const meta = response?.meta || { total: 0, totalPages: 1 };
      
      setTransactions(Array.isArray(data) ? data : []);
      setTotalItems(meta.total || 0);
      setTotalPages(meta.totalPages || 1);
      
      // Refresh stats after fetching transactions
      fetchStats();
    } catch (err) {
      console.error('Failed to fetch transactions:', err);
      setError('Gagal memuat data transaksi');
      setTransactions([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchTransactions();
  }, [currentPage, statusFilter, companyFilter, dateFrom, dateTo]);

  // Handle search with debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      if (currentPage === 1) {
        fetchTransactions();
      } else {
        setCurrentPage(1);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Update transaction status
  const updateStatus = async (transactionId: string, newStatus: TransactionStatus) => {
    try {
      await transactionsApi.update(transactionId, { status: newStatus });
      
      // Update local state
      setTransactions(prev => 
        prev.map(tx => 
          tx.transaction_id === transactionId 
            ? { ...tx, status: newStatus }
            : tx
        )
      );
      
      // Remove from selected if it was selected
      setSelectedIds(prev => {
        const next = new Set(prev);
        next.delete(transactionId);
        return next;
      });
    } catch (err) {
      console.error('Failed to update transaction:', err);
      alert('Gagal mengubah status transaksi');
    }
  };

  // Bulk approve selected transactions
  const bulkApprove = async () => {
    if (selectedIds.size === 0) return;
    
    const confirmed = confirm(`Setujui ${selectedIds.size} transaksi yang dipilih?`);
    if (!confirmed) return;

    try {
      await transactionsApi.bulkReconcile(Array.from(selectedIds));
      
      // Update local state
      setTransactions(prev =>
        prev.map(tx =>
          selectedIds.has(tx.transaction_id)
            ? { ...tx, status: 'RECONCILED' as const }
            : tx
        )
      );
      setSelectedIds(new Set());
    } catch (err) {
      console.error('Failed to bulk approve:', err);
      alert('Gagal menyetujui transaksi');
    }
  };

  // Toggle selection
  const toggleSelect = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  // Select all visible
  const selectAll = () => {
    const pendingIds = transactions
      .filter(tx => tx.status === 'PENDING')
      .map(tx => tx.transaction_id);
    setSelectedIds(new Set(pendingIds));
  };

  // Clear selection
  const clearSelection = () => {
    setSelectedIds(new Set());
  };

  // Format currency
  const formatCurrency = (amount: number, currencyCode: string = 'IDR') => {
    const locale = currencyCode === 'IDR' ? 'id-ID' : 'en-US';
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: currencyCode === 'IDR' ? 0 : 2,
      maximumFractionDigits: currencyCode === 'IDR' ? 0 : 2,
    }).format(amount);
  };

  // Format date
  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('id-ID', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  // Get status badge
  const getStatusBadge = (status: Transaction['status']) => {
    switch (status) {
      case 'PENDING':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
            <Clock className="w-3 h-3" />
            Menunggu
          </span>
        );
      case 'RECONCILED':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
            <CheckCircle2 className="w-3 h-3" />
            Disetujui
          </span>
        );
      case 'FLAGGED':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
            <XCircle className="w-3 h-3" />
            Ditolak
          </span>
        );
    }
  };

  // Count by status
  const pendingCount = transactions.filter(tx => tx.status === 'PENDING').length;

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Manajemen Transaksi</h1>
          <p className="text-slate-600 mt-1">
            Tinjau dan setujui transaksi yang masuk
          </p>
        </div>
        
        <button
          onClick={fetchTransactions}
          className="inline-flex items-center gap-2 px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg transition-colors"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-yellow-100 rounded-lg">
              <Clock className="w-5 h-5 text-yellow-600" />
            </div>
            <div>
              <p className="text-sm text-yellow-600">Menunggu Persetujuan</p>
              <p className="text-2xl font-bold text-yellow-700">{stats.pending}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-green-50 border border-green-200 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <CheckCircle2 className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-green-600">Disetujui</p>
              <p className="text-2xl font-bold text-green-700">{stats.reconciled}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-red-50 border border-red-200 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-100 rounded-lg">
              <AlertTriangle className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <p className="text-sm text-red-600">Ditolak</p>
              <p className="text-2xl font-bold text-red-700">{stats.flagged}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <div className="flex flex-wrap gap-4">
          {/* Search */}
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                placeholder="Cari deskripsi..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          {/* Status Filter */}
          <div className="min-w-[150px]">
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value as StatusFilter);
                setCurrentPage(1);
              }}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="ALL">Semua Status</option>
              <option value="PENDING">Menunggu</option>
              <option value="RECONCILED">Disetujui</option>
              <option value="FLAGGED">Ditolak</option>
            </select>
          </div>

          {/* Company Filter */}
          <div className="min-w-[200px]">
            <select
              value={companyFilter}
              onChange={(e) => {
                setCompanyFilter(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">Semua Perusahaan</option>
              {companies.map((company) => (
                <option key={company.company_id} value={company.company_id}>
                  {company.name}
                </option>
              ))}
            </select>
          </div>

          {/* Date Range */}
          <div className="flex items-center gap-2">
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => {
                setDateFrom(e.target.value);
                setCurrentPage(1);
              }}
              className="px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            <span className="text-slate-400">-</span>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => {
                setDateTo(e.target.value);
                setCurrentPage(1);
              }}
              className="px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>
      </div>

      {/* Bulk Actions */}
      {selectedIds.size > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-center justify-between">
          <span className="text-blue-800">
            <strong>{selectedIds.size}</strong> transaksi dipilih
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={clearSelection}
              className="px-3 py-1.5 text-sm text-slate-600 hover:text-slate-800"
            >
              Batal
            </button>
            <button
              onClick={bulkApprove}
              className="px-4 py-1.5 text-sm bg-green-600 hover:bg-green-700 text-white rounded-lg flex items-center gap-2"
            >
              <CheckCircle2 className="w-4 h-4" />
              Setujui Semua
            </button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-2 text-slate-500">Memuat data...</p>
          </div>
        ) : error ? (
          <div className="p-8 text-center text-red-600">
            <AlertTriangle className="w-8 h-8 mx-auto mb-2" />
            <p>{error}</p>
          </div>
        ) : transactions.length === 0 ? (
          <div className="p-8 text-center text-slate-500">
            <Clock className="w-12 h-12 mx-auto mb-2 text-slate-300" />
            <p>Tidak ada transaksi ditemukan</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="px-4 py-3 text-left">
                      <input
                        type="checkbox"
                        checked={selectedIds.size > 0 && selectedIds.size === pendingCount}
                        onChange={(e) => e.target.checked ? selectAll() : clearSelection()}
                        className="rounded border-slate-300"
                      />
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                      Tanggal
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                      Perusahaan
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                      Deskripsi
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">
                      Disubmit Oleh
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-slate-600 uppercase tracking-wider">
                      Gambar
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600 uppercase tracking-wider">
                      Jumlah Unit
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600 uppercase tracking-wider">
                      Jumlah
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-slate-600 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-slate-600 uppercase tracking-wider">
                      Aksi
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {transactions.map((tx) => (
                    <tr 
                      key={tx.transaction_id} 
                      className={`hover:bg-slate-50 ${selectedIds.has(tx.transaction_id) ? 'bg-blue-50' : ''}`}
                    >
                      <td className="px-4 py-3">
                        {tx.status === 'PENDING' && (
                          <input
                            type="checkbox"
                            checked={selectedIds.has(tx.transaction_id)}
                            onChange={() => toggleSelect(tx.transaction_id)}
                            className="rounded border-slate-300"
                          />
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-600">
                        {formatDate(tx.date)}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <Building2 className="w-4 h-4 text-slate-400" />
                          <span className="text-sm font-medium text-slate-900">
                            {tx.company?.name || '-'}
                          </span>
                        </div>
                        {tx.bankAccount && (
                          <p className="text-xs text-slate-500 mt-0.5">
                            {tx.bankAccount.bankName} - {tx.bankAccount.accountNumber}
                          </p>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-sm text-slate-900">{tx.description}</p>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-sm text-slate-600">{tx.submittedBy?.name || '-'}</p>
                      </td>
                      <td className="px-4 py-3 text-center">
                        {tx.imageUrl ? (
                          <a 
                            href={tx.imageUrl} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="inline-block"
                          >
                            <img 
                              src={tx.imageUrl} 
                              alt="Transaction" 
                              className="w-10 h-10 object-cover rounded-lg border border-slate-200 hover:border-blue-400 transition-colors cursor-pointer"
                            />
                          </a>
                        ) : (
                          <div className="w-10 h-10 mx-auto bg-slate-100 rounded-lg flex items-center justify-center">
                            <ImageIcon className="w-4 h-4 text-slate-400" />
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {tx.units !== null ? (
                          <span className="text-sm font-medium text-slate-700">
                            {new Intl.NumberFormat('id-ID', { maximumFractionDigits: 4 }).format(tx.units)}
                          </span>
                        ) : (
                          <span className="text-sm text-slate-400">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className={`flex items-center justify-end gap-1 font-medium ${
                          tx.type === 'CREDIT' ? 'text-green-600' : 'text-red-600'
                        }`}>
                          {tx.type === 'CREDIT' ? (
                            <ArrowUpCircle className="w-4 h-4" />
                          ) : (
                            <ArrowDownCircle className="w-4 h-4" />
                          )}
                          {formatCurrency(tx.amount, tx.bankAccount?.currency || 'IDR')}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center">
                        {getStatusBadge(tx.status)}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-center">
                          <button
                            onClick={() => setSelectedTransaction(tx)}
                            className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            title="Lihat Detail"
                          >
                            <Eye className="w-5 h-5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="px-4 py-3 border-t border-slate-200 flex items-center justify-between">
              <p className="text-sm text-slate-600">
                Menampilkan {((currentPage - 1) * pageSize) + 1} - {Math.min(currentPage * pageSize, totalItems)} dari {totalItems} transaksi
              </p>
              
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="p-2 text-slate-600 hover:bg-slate-100 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                
                <span className="px-3 py-1 text-sm text-slate-600">
                  Halaman {currentPage} dari {totalPages}
                </span>
                
                <button
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="p-2 text-slate-600 hover:bg-slate-100 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Transaction Detail Modal */}
      {selectedTransaction && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* Backdrop */}
          <div 
            className="fixed inset-0 bg-black/50 backdrop-blur-sm" 
            onClick={() => setSelectedTransaction(null)} 
          />
          
          {/* Modal Content */}
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto animate-fade-in-up">
            {/* Header */}
            <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between rounded-t-2xl">
              <div>
                <h3 className="text-xl font-bold text-slate-900">Detail Transaksi</h3>
                <p className="text-sm text-slate-500 mt-0.5">
                  ID: {selectedTransaction.transaction_id.slice(0, 8)}...
                </p>
              </div>
              <button 
                onClick={() => setSelectedTransaction(null)}
                className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Body */}
            <div className="p-6 space-y-6">
              {/* Status Badge */}
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-500">Status</span>
                {getStatusBadge(selectedTransaction.status)}
              </div>

              {/* Transaction Info Grid */}
              <div className="grid grid-cols-2 gap-4">
                {/* Date */}
                <div className="bg-slate-50 rounded-xl p-4">
                  <div className="flex items-center gap-2 text-slate-500 mb-1">
                    <Calendar className="w-4 h-4" />
                    <span className="text-xs font-medium uppercase">Tanggal</span>
                  </div>
                  <p className="text-slate-900 font-medium">{formatDate(selectedTransaction.date)}</p>
                </div>

                {/* Amount */}
                <div className="bg-slate-50 rounded-xl p-4">
                  <div className="flex items-center gap-2 text-slate-500 mb-1">
                    <CreditCard className="w-4 h-4" />
                    <span className="text-xs font-medium uppercase">Jumlah</span>
                  </div>
                  <p className={`font-bold text-lg ${selectedTransaction.type === 'CREDIT' ? 'text-green-600' : 'text-red-600'}`}>
                    {selectedTransaction.type === 'CREDIT' ? '+' : '-'} {formatCurrency(selectedTransaction.amount, selectedTransaction.bankAccount?.currency || 'IDR')}
                  </p>
                </div>

                {/* Units */}
                {selectedTransaction.units !== null && (
                  <div className="bg-slate-50 rounded-xl p-4">
                    <div className="flex items-center gap-2 text-slate-500 mb-1">
                      <FileText className="w-4 h-4" />
                      <span className="text-xs font-medium uppercase">Jumlah Unit</span>
                    </div>
                    <p className="text-slate-900 font-bold text-lg">
                      {new Intl.NumberFormat('id-ID', { maximumFractionDigits: 4 }).format(selectedTransaction.units)}
                    </p>
                  </div>
                )}

                {/* Company */}
                <div className="bg-slate-50 rounded-xl p-4">
                  <div className="flex items-center gap-2 text-slate-500 mb-1">
                    <Building2 className="w-4 h-4" />
                    <span className="text-xs font-medium uppercase">Perusahaan</span>
                  </div>
                  <p className="text-slate-900 font-medium">{selectedTransaction.company?.name || '-'}</p>
                </div>

                {/* Bank Account */}
                <div className="bg-slate-50 rounded-xl p-4">
                  <div className="flex items-center gap-2 text-slate-500 mb-1">
                    <CreditCard className="w-4 h-4" />
                    <span className="text-xs font-medium uppercase">Rekening Bank</span>
                  </div>
                  {selectedTransaction.bankAccount ? (
                    <>
                      <p className="text-slate-900 font-medium">{selectedTransaction.bankAccount.bankName}</p>
                      <p className="text-sm text-slate-500">{selectedTransaction.bankAccount.accountNumber}</p>
                    </>
                  ) : (
                    <p className="text-slate-400">-</p>
                  )}
                </div>
              </div>

              {/* Description */}
              <div className="bg-slate-50 rounded-xl p-4">
                <div className="flex items-center gap-2 text-slate-500 mb-2">
                  <FileText className="w-4 h-4" />
                  <span className="text-xs font-medium uppercase">Deskripsi</span>
                </div>
                <p className="text-slate-900">{selectedTransaction.description}</p>
              </div>

              {/* Submitted By */}
              <div className="bg-slate-50 rounded-xl p-4">
                <div className="flex items-center gap-2 text-slate-500 mb-1">
                  <User className="w-4 h-4" />
                  <span className="text-xs font-medium uppercase">Disubmit Oleh</span>
                </div>
                <p className="text-slate-900 font-medium">{selectedTransaction.submittedBy?.name || '-'}</p>
              </div>

              {/* Reference */}
              {selectedTransaction.reference && (
                <div className="bg-slate-50 rounded-xl p-4">
                  <div className="flex items-center gap-2 text-slate-500 mb-2">
                    <FileText className="w-4 h-4" />
                    <span className="text-xs font-medium uppercase">Referensi</span>
                  </div>
                  <p className="text-slate-900 font-mono text-sm">{selectedTransaction.reference}</p>
                </div>
              )}

              {/* Proof Image */}
              <div className="bg-slate-50 rounded-xl p-4">
                <div className="flex items-center gap-2 text-slate-500 mb-3">
                  <ImageIcon className="w-4 h-4" />
                  <span className="text-xs font-medium uppercase">Bukti Transaksi</span>
                </div>
                {selectedTransaction.imageUrl ? (
                  <a 
                    href={selectedTransaction.imageUrl} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="block"
                  >
                    <img 
                      src={selectedTransaction.imageUrl} 
                      alt="Bukti Transaksi" 
                      className="w-full max-h-96 object-contain rounded-lg border border-slate-200 hover:border-blue-400 transition-colors cursor-pointer"
                    />
                    <p className="text-xs text-center text-blue-600 mt-2 hover:underline">
                      Klik untuk melihat ukuran penuh
                    </p>
                  </a>
                ) : (
                  <div className="flex flex-col items-center justify-center py-8 text-slate-400">
                    <ImageIcon className="w-12 h-12 mb-2" />
                    <p className="text-sm">Tidak ada bukti transaksi</p>
                  </div>
                )}
              </div>

              {/* Timestamps */}
              <div className="flex items-center justify-between text-xs text-slate-400 pt-4 border-t border-slate-100">
                <span>Dibuat: {new Date(selectedTransaction.createdAt).toLocaleString('id-ID')}</span>
                <span>Diperbarui: {new Date(selectedTransaction.updatedAt).toLocaleString('id-ID')}</span>
              </div>
            </div>

            {/* Footer Actions */}
            {selectedTransaction.status === 'PENDING' && (
              <div className="sticky bottom-0 bg-slate-50 border-t border-slate-200 px-6 py-4 flex items-center justify-end gap-3 rounded-b-2xl">
                <button
                  onClick={() => {
                    updateStatus(selectedTransaction.transaction_id, 'FLAGGED');
                    setSelectedTransaction(null);
                  }}
                  className="px-4 py-2 text-red-600 hover:bg-red-50 border border-red-200 rounded-lg transition-colors flex items-center gap-2"
                >
                  <XCircle className="w-4 h-4" />
                  Tolak
                </button>
                <button
                  onClick={() => {
                    updateStatus(selectedTransaction.transaction_id, 'RECONCILED');
                    setSelectedTransaction(null);
                  }}
                  className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors flex items-center gap-2"
                >
                  <CheckCircle2 className="w-4 h-4" />
                  Setujui
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default TransactionManager;
